"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { EntityDisplay } from "fansunited-sports-ui";
import type { FUSportsLineupMember, FUSportsCompetitorLineup } from "fansunited-data-layer";
import { TeamLogo } from "@/components/ui/TeamLogo";
import type { MatchLineupFieldProps } from "./MatchLineupFieldDesktop";
import { MatchLineupFieldPlayerMarker } from "./MatchLineupFieldPlayerMarker";
import { MatchLineupFieldHalfPitch } from "./MatchLineupFieldHalfPitch";
import type { PlayerEventsMap } from "./playerEventsHelper";

/**
 * Position players on vertical half-pitch
 */
const PositionedPlayers: React.FC<{
  lineup: FUSportsCompetitorLineup;
  color: string;
  playerEventsMap: PlayerEventsMap;
}> = t0 => {
  const $ = _c(6);
  const {
    lineup,
    color,
    playerEventsMap
  } = t0;
  let t1;
  if ($[0] !== color || $[1] !== lineup.starters || $[2] !== playerEventsMap) {
    const starters = lineup.starters.filter(_temp);
    const rows = new Map();
    starters.forEach(player => {
      const row = player.positionX;
      if (!rows.has(row)) {
        rows.set(row, []);
      }
      rows.get(row).push(player);
    });
    rows.forEach(_temp3);
    const sortedRows = Array.from(rows.keys()).sort(_temp4);
    const rowCount = sortedRows.length;
    t1 = sortedRows.map((posX, rowIndex) => {
      const players_0 = rows.get(posX);
      const yPercent = 8 + rowIndex / Math.max(rowCount - 1, 1) * 80;
      return players_0.map((player_0, idx) => {
        const count = players_0.length;
        const xPercent = 2 + (idx + 0.5) / count * 96;
        const events = playerEventsMap.get(player_0.id);
        return <div key={player_0.id} className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{
          left: `${xPercent}%`,
          top: `${yPercent}%`
        }}><MatchLineupFieldPlayerMarker player={player_0} color={color} events={events} /></div>;
      });
    });
    $[0] = color;
    $[1] = lineup.starters;
    $[2] = playerEventsMap;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <>{t1}</>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};

/**
 * Mobile version - Vertical half-pitch (controlled by parent via activeTeam prop)
 */
export const MatchLineupFieldMobile: React.FC<MatchLineupFieldProps> = t0 => {
  const $ = _c(21);
  const {
    homeTeamName,
    awayTeamName,
    homeTeamLogo,
    awayTeamLogo,
    homeTeamColor: t1,
    awayTeamColor: t2,
    homeTeamCountryCode,
    homeTeamCountryFlag,
    homeTeamPrimaryColor,
    homeTeamSecondaryColor,
    awayTeamCountryCode,
    awayTeamCountryFlag,
    awayTeamPrimaryColor,
    awayTeamSecondaryColor,
    homeLineup,
    awayLineup,
    homeHasPositions,
    awayHasPositions,
    activeTeam: t3,
    playerEventsMap
  } = t0;
  const homeTeamColor = t1 === undefined ? "#1e40af" : t1;
  const awayTeamColor = t2 === undefined ? "#dc2626" : t2;
  const activeTeam = t3 === undefined ? "home" : t3;
  const isHome = activeTeam === "home";
  const currentTeamName = isHome ? homeTeamName : awayTeamName;
  const currentTeamLogo = isHome ? homeTeamLogo : awayTeamLogo;
  const currentLineup = isHome ? homeLineup : awayLineup;
  const currentColor = isHome ? homeTeamColor : awayTeamColor;
  const hasPositions = isHome ? homeHasPositions : awayHasPositions;
  const currentFormation = currentLineup.formation;
  const currentCountryCode = isHome ? homeTeamCountryCode : awayTeamCountryCode;
  const currentCountryFlag = isHome ? homeTeamCountryFlag : awayTeamCountryFlag;
  const currentPrimaryColor = isHome ? homeTeamPrimaryColor : awayTeamPrimaryColor;
  const currentSecondaryColor = isHome ? homeTeamSecondaryColor : awayTeamSecondaryColor;
  const t4 = currentFormation ?? undefined;
  let t5;
  if ($[0] !== currentCountryCode || $[1] !== currentCountryFlag || $[2] !== currentPrimaryColor || $[3] !== currentSecondaryColor || $[4] !== currentTeamLogo) {
    t5 = <TeamLogo src={currentTeamLogo} size="md" countryCode={currentCountryCode} countryFlag={currentCountryFlag} primaryColor={currentPrimaryColor} secondaryColor={currentSecondaryColor} />;
    $[0] = currentCountryCode;
    $[1] = currentCountryFlag;
    $[2] = currentPrimaryColor;
    $[3] = currentSecondaryColor;
    $[4] = currentTeamLogo;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      imageSize: "md",
      typography: {
        name: "text-sm font-semibold text-foreground truncate",
        subtitle: "text-xs text-muted-foreground"
      },
      container: "mb-2"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== currentTeamName || $[8] !== t4 || $[9] !== t5) {
    t7 = <EntityDisplay name={currentTeamName} subtitle={t4} image={t5} styles={t6} />;
    $[7] = currentTeamName;
    $[8] = t4;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== currentColor || $[12] !== currentLineup || $[13] !== hasPositions || $[14] !== playerEventsMap) {
    t8 = hasPositions && <PositionedPlayers lineup={currentLineup} color={currentColor} playerEventsMap={playerEventsMap} />;
    $[11] = currentColor;
    $[12] = currentLineup;
    $[13] = hasPositions;
    $[14] = playerEventsMap;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <MatchLineupFieldHalfPitch>{t8}</MatchLineupFieldHalfPitch>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <div><div className="bg-card rounded-md shadow-md overflow-hidden border border-border p-3">{t7}{t9}</div></div>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};
function _temp(p) {
  return p.positionX && p.positionY;
}
function _temp2(a, b) {
  return (a.positionY || 0) - (b.positionY || 0);
}
function _temp3(players) {
  return players.sort(_temp2);
}
function _temp4(a_0, b_0) {
  return a_0 - b_0;
}